import React from 'react';
import Box from '@amzn/awsui-components-core/box';
import ExpandableSection from '@amzn/awsui-components-core/expandable-section';
import KeyValuePairs from '@amzn/awsui-components-core/key-value-pairs';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import Table from '@amzn/awsui-components-core/table';
import { QuerySqlBlock } from 'src/components/star-grid/components/QuerySqlBlock';
import { useStarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import type { PnlQueryAnalysis } from 'src/components/star-grid/queryStats.types';
import { formatDurationMs } from 'src/components/star-grid/utils/reportStatus';

/** Where the report's fact scan spent its time and memory, and the plan StarRocks chose for it. */
export const ReportQueryAnalysis: React.FC<{ analysis?: PnlQueryAnalysis; plans?: readonly string[] }> = ({ analysis, plans }) => {
  const text = useStarGridText();
  const summary = analysis?.summary;
  return (
    <SpaceBetween size="s">
      {analysis && !analysis.available && <Box color="text-status-inactive">{text('statsProfileGone')}</Box>}
      {summary && (
        <>
          <KeyValuePairs
            columns={4}
            items={[
              { label: text('statsTotalTime'), value: formatDurationMs(summary.total_ms) },
              {
                label: text('statsExecution'),
                value: [
                  formatDurationMs(summary.execution_ms),
                  ...summary.execution_parts.filter((part) => part.ms).map((part) => `${part.part} ${formatDurationMs(part.ms)}`)
                ].join(' · ')
              },
              { label: text('statsPeakMemory'), value: summary.peak_memory ?? '—' },
              { label: text('statsAllocatedMemory'), value: summary.allocated_memory ?? '—' }
            ]}
          />
          <Table
            variant="embedded"
            header={<Box variant="h4">{text('statsStages')}</Box>}
            items={[...summary.stages]}
            columnDefinitions={[
              { id: 'stage', header: text('statsStage'), cell: (row) => row.stage },
              { id: 'ms', header: text('statsStageTime'), cell: (row) => formatDurationMs(row.ms) },
              { id: 'pct', header: text('statsShare'), cell: (row) => `${row.pct}%` }
            ]}
          />
          <Table
            variant="embedded"
            header={<Box variant="h4">{text('statsTopOperators')}</Box>}
            items={[...summary.top_nodes]}
            columnDefinitions={[
              { id: 'node', header: text('statsOperator'), cell: (row) => row.node },
              { id: 'ms', header: text('statsStageTime'), cell: (row) => formatDurationMs(row.ms) },
              { id: 'pct', header: text('statsShare'), cell: (row) => `${row.pct}%` }
            ]}
          />
        </>
      )}
      {analysis?.text && (
        <ExpandableSection headerText={text('statsExecutionProfile')}>
          <QuerySqlBlock sql={analysis.text} />
        </ExpandableSection>
      )}
      {plans?.map((plan, index) => (
        // eslint-disable-next-line react/no-array-index-key -- plans come in scan order and have no id
        <ExpandableSection key={`plan-${index}`} headerText={`${text('statsPlan')} #${index + 1}`}>
          <QuerySqlBlock sql={plan} />
        </ExpandableSection>
      ))}
    </SpaceBetween>
  );
};
